import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  TextField,
  Autocomplete,
  Chip,
  FormControl,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Send as SendIcon,
  ArrowBack as BackIcon,
  CheckCircle as SuccessIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import assessmentService from '../services/assessmentService';
import { studentService } from '../services/studentService';
import teamService from '../services/teamService';
import { useAuth } from '../contexts/AuthContext';

interface Assessment {
  id: number;
  title: string;
  description: string;
  category: string;
  totalQuestions: number;
  estimatedMinutes: number;
  isActive: boolean;
}

interface Student {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  schoolId?: string;
  universityName?: string;
}

interface Team {
  id: number;
  name: string;
  departmentName?: string;
  memberCount?: number;
}

const AssignSurveyPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedAssessment, setSelectedAssessment] = useState<Assessment | null>(null);
  const [assignmentType, setAssignmentType] = useState<'individual' | 'group'>('individual');
  const [selectedStudents, setSelectedStudents] = useState<Student[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [dueDate, setDueDate] = useState<Date | null>(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)); // 7 days from now
  const [notes, setNotes] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const steps = ['Select Assessment', 'Choose Recipients', 'Set Details', 'Review & Assign'];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [assessmentsRes, studentsRes, teamsRes] = await Promise.all([
        assessmentService.getAssessments(0, 100),
        studentService.getStudents(),
        teamService.getMyTeams()
      ]);

      // Only show active assessments
      setAssessments(assessmentsRes.content.filter((a: Assessment) => a.isActive));
      setStudents(studentsRes.content || []);
      setTeams(teamsRes);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0 && !selectedAssessment) {
      toast.error('Please select an assessment');
      return;
    }
    if (activeStep === 1) {
      if (assignmentType === 'individual' && selectedStudents.length === 0) {
        toast.error('Please select at least one student');
        return;
      }
      if (assignmentType === 'group' && !selectedTeam) {
        toast.error('Please select a team');
        return;
      }
    }
    if (activeStep === 2 && !dueDate) {
      toast.error('Please set a due date');
      return;
    }
    
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleAssign = async () => {
    if (!selectedAssessment || !dueDate) return;

    try {
      setAssigning(true);
      
      // Get student IDs
      let studentIds: number[] = [];
      
      if (assignmentType === 'individual') {
        studentIds = selectedStudents.map(s => s.id);
      } else if (assignmentType === 'group' && selectedTeam) {
        // For group assignment, we need to get team members from the backend
        // For now, let's handle this by making a separate call if needed
        // This is a simplified version - you may need team member endpoint
        toast.error('Group assignment feature needs team member endpoint implementation');
        return;
      }

      // Validate we have students
      if (studentIds.length === 0) {
        toast.error('No students selected for assignment');
        return;
      }

      // Format the data according to backend expectations
      const assignmentData = {
        studentIds,
        dueDate: dueDate.toISOString(), // Send full ISO string, backend will parse
        notes: notes || undefined,
        sendNotification: true
      };

      console.log('Sending assignment data:', assignmentData);
      
      await assessmentService.assignAssessment(selectedAssessment.id, assignmentData);
      
      const recipientCount = studentIds.length;
      toast.success(`Assessment assigned successfully to ${recipientCount} ${recipientCount === 1 ? 'student' : 'students'}!`);
      navigate('/manage-surveys');
    } catch (error: any) {
      console.error('Error assigning assessment:', error);
      const message = error.response?.data?.message || error.message || 'Failed to assign assessment';
      toast.error(message);
    } finally {
      setAssigning(false);
      setConfirmDialog(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select an Assessment to Assign
            </Typography>
            {loading ? (
              <CircularProgress />
            ) : assessments.length === 0 ? (
              <Alert severity="info">No active assessments available</Alert>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="center">Questions</TableCell>
                      <TableCell align="center">Est. Time</TableCell>
                      <TableCell align="center">Select</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assessments.map((assessment) => (
                      <TableRow 
                        key={assessment.id}
                        selected={selectedAssessment?.id === assessment.id}
                        hover
                        onClick={() => setSelectedAssessment(assessment)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Typography variant="subtitle2">{assessment.title}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {assessment.description}
                          </Typography>
                        </TableCell>
                        <TableCell>{assessment.category}</TableCell>
                        <TableCell align="center">{assessment.totalQuestions}</TableCell>
                        <TableCell align="center">{assessment.estimatedMinutes} min</TableCell>
                        <TableCell align="center">
                          <Checkbox 
                            checked={selectedAssessment?.id === assessment.id}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Choose Recipients
            </Typography>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <Box display="flex" gap={2}>
                <Button
                  variant={assignmentType === 'individual' ? 'contained' : 'outlined'}
                  startIcon={<PersonIcon />}
                  onClick={() => setAssignmentType('individual')}
                >
                  Individual Students
                </Button>
                <Button
                  variant={assignmentType === 'group' ? 'contained' : 'outlined'}
                  startIcon={<GroupIcon />}
                  onClick={() => setAssignmentType('group')}
                >
                  Entire Team
                </Button>
              </Box>
            </FormControl>

            {assignmentType === 'individual' ? (
              <Autocomplete
                multiple
                options={students}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName}${option.schoolId ? ` (${option.schoolId})` : ''}`}
                value={selectedStudents}
                onChange={(_, newValue) => setSelectedStudents(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Select Students"
                    placeholder="Search by name or ID"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={`${option.firstName} ${option.lastName}`}
                      {...getTagProps({ index })}
                    />
                  ))
                }
              />
            ) : (
              <Autocomplete
                options={teams}
                getOptionLabel={(option) => `${option.name} (${option.memberCount || 0} members)`}
                value={selectedTeam}
                onChange={(_, newValue) => setSelectedTeam(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Select Team"
                    placeholder="Choose a team"
                  />
                )}
              />
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Set Assignment Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Due Date"
                    value={dueDate}
                    onChange={(newValue) => setDueDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true
                      }
                    }}
                    minDate={new Date()}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Notes (Optional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any instructions or notes for the students..."
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Assignment
            </Typography>
            <Card>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Assessment</Typography>
                    <Typography variant="body1">
                      {selectedAssessment?.title}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Recipients</Typography>
                    <Typography variant="body1">
                      {assignmentType === 'individual' 
                        ? `${selectedStudents.length} student(s) selected`
                        : `Team: ${selectedTeam?.name} (${selectedTeam?.memberCount || 0} members)`
                      }
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Due Date</Typography>
                    <Typography variant="body1">
                      {dueDate?.toLocaleDateString()}
                    </Typography>
                  </Grid>
                  {notes && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                      <Typography variant="body1">{notes}</Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
            {assignmentType === 'individual' && selectedStudents.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>Selected Students:</Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {selectedStudents.map((student) => (
                    <Chip 
                      key={student.id} 
                      label={`${student.firstName} ${student.lastName}`}
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button 
          startIcon={<BackIcon />} 
          onClick={() => navigate('/manage-surveys')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" component="h1">
          Assign Assessment
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card>
        <CardContent sx={{ minHeight: 400 }}>
          {renderStepContent()}
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
        >
          Back
        </Button>
        {activeStep === steps.length - 1 ? (
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => setConfirmDialog(true)}
          >
            Assign Assessment
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
          >
            Next
          </Button>
        )}
      </Box>

      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>Confirm Assignment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to assign "{selectedAssessment?.title}" to{' '}
            {assignmentType === 'individual' 
              ? `${selectedStudents.length} student(s)`
              : `all members of ${selectedTeam?.name}`
            }?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAssign}
            disabled={assigning}
            startIcon={assigning ? <CircularProgress size={20} /> : <SuccessIcon />}
          >
            {assigning ? 'Assigning...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AssignSurveyPage;